import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Minus, AlertTriangle } from "lucide-react";
import Wrapper from "../../../components/Wrapper";
import { labApi } from "../../../services/labApi";

const isLowStock = (item) =>
  item.preparation_mode !== "instant" &&
  Number(item.current_stock || 0) <= Number(item.min_quantity || 0);

export default function StockPage() {
  const [stock, setStock] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustType, setAdjustType] = useState("add");

  const load = async () => {
    const reagents = await labApi.getReagents();
    setStock(reagents);
  };

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const filteredStock = useMemo(
    () => stock.filter((item) => item.name.toLowerCase().includes(search.toLowerCase())),
    [stock, search]
  );
  const sortedStock = useMemo(
    () =>
      [...filteredStock].sort((a, b) => {
        const aInstant = a.preparation_mode === "instant";
        const bInstant = b.preparation_mode === "instant";
        if (aInstant !== bInstant) return aInstant ? 1 : -1;
        const stockDiff = Number(a.current_stock || 0) - Number(b.current_stock || 0);
        return stockDiff !== 0 ? stockDiff : a.name.localeCompare(b.name);
      }),
    [filteredStock]
  );
  const lowStockCount = useMemo(
    () => filteredStock.filter(isLowStock).length,
    [filteredStock]
  );

  const openAdjustModal = (item, type) => {
    if (item.preparation_mode === "instant") return;
    setSelectedStock(item);
    setAdjustType(type);
    setAdjustAmount("");
    setShowAdjustModal(true);
  };

  const handleAdjust = async () => {
    if (!selectedStock || !adjustAmount) return;
    await labApi.adjustReagentStock(selectedStock.id, adjustType, Number(adjustAmount));
    setShowAdjustModal(false);
    await load();
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="w-full text-left">
          <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Stock</h2>
          <p className="mt-1 text-slate-500 text-left">Live reagent inventory</p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search reagents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg"
          />
        </div>

        {lowStockCount > 0 ? (
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle size={16} />
            <span>{lowStockCount} reagent{lowStockCount === 1 ? "" : "s"} below minimum</span>
          </div>
        ) : null}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-right font-medium">Stock</th>
                <th className="px-3 py-2 text-right font-medium">Min</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedStock.map((item) => {
                const low = isLowStock(item);
                const instant = item.preparation_mode === "instant";
                return (
                  <tr
                    key={item.id}
                    className={
                      low
                        ? "bg-red-50 hover:bg-red-100/60"
                        : instant
                          ? "bg-violet-50/40 hover:bg-violet-50"
                          : "hover:bg-slate-50"
                    }
                  >
                    <td className="px-3 py-2 font-medium text-slate-800">{item.name}</td>
                    <td className="px-3 py-2 text-slate-500 capitalize">
                      {String(item.type_id || "").replace(/_/g, " ")}
                      {instant ? " · Instant" : ""}
                    </td>
                    <td className={`px-3 py-2 text-right font-semibold tabular-nums ${low ? "text-red-700" : "text-slate-800"}`}>
                      {instant ? "—" : Number(item.current_stock || 0)}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500 tabular-nums">
                      {instant ? "—" : Number(item.min_quantity || 0)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {instant ? (
                        <span className="text-xs text-slate-400">Chemical stock</span>
                      ) : (
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() => openAdjustModal(item, "add")}
                            className="h-7 w-7 bg-emerald-100 text-emerald-600 rounded inline-flex items-center justify-center hover:bg-emerald-200"
                          >
                            <Plus size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openAdjustModal(item, "subtract")}
                            className="h-7 w-7 bg-red-100 text-red-600 rounded inline-flex items-center justify-center hover:bg-red-200"
                          >
                            <Minus size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sortedStock.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-500">No reagents found</p>
          ) : null}
        </div>

        {showAdjustModal && selectedStock && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-sm p-4 space-y-3">
              <h3 className="text-lg font-semibold text-slate-800">{adjustType === "add" ? "Add Stock" : "Remove Stock"}</h3>
              <p className="text-sm text-slate-500">{selectedStock.name}</p>
              <input
                type="number"
                min="1"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="Enter quantity"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              />
              <div className="flex gap-3">
                <button onClick={() => setShowAdjustModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg">
                  Cancel
                </button>
                <button onClick={handleAdjust} className={`flex-1 px-4 py-2 text-white rounded-lg ${adjustType === "add" ? "bg-emerald-600" : "bg-red-600"}`}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
